import { EnvelopeLabelPlacement } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

/**
 * Horizontal alignment of a label's text relative to its anchor point. Expressed as start/center/end (rather than
 * left/right) because the label is flipped 180° for readability when upside down, which swaps the start and end sides.
 */
export type LabelTextAlignment = 'start' | 'center' | 'end';

/**
 * Distance in from an end at which a `Left`/`Right` label is anchored.
 * The PDF/DXF outputs hard-code their own offset (6 inches) for the same
 * purpose; the two paths have always differed slightly.
 */
const OFFSET_FROM_END = 8;

/**
 * Computes where a truss's name label should be anchored along its center segment for a given `placement`, returning
 * the offset from the segment's start (its left end) and the text's alignment relative to that anchor. Mirrors the
 * server-side `GetLabelOffsetAndAlignment` logic.
 */
export function getLabelOffsetAndAlignment(
  placement: EnvelopeLabelPlacement,
  segmentLength: number,
): { offset: number; alignment: LabelTextAlignment } {
  const endOffset = Math.min(OFFSET_FROM_END, segmentLength / 2);
  const midpointOffset = segmentLength / 2;

  switch (placement) {
    case 'Left':
      return { offset: endOffset, alignment: 'start' };
    case 'CenterLeft':
      return { offset: midpointOffset, alignment: 'end' };
    case 'Center':
      return { offset: midpointOffset, alignment: 'center' };
    case 'CenterRight':
      return { offset: midpointOffset, alignment: 'start' };
    case 'Right':
      return { offset: segmentLength - endOffset, alignment: 'end' };
    default:
      return assertExhaustiveSwitch(placement);
  }
}
